import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import UserLayout from "../../components/user/dashboard/UserLayout";
import { MyRentalsDetailSkeleton } from "../../components/user/dashboard/MyRentalsSkeleton";
import { useUserRentals } from "../../services/user/UserRentalsService";
import {
  ArrowLeft,
  Download,
  AlertCircle,
  Loader2,
  MapPin,
  Map,
  Calendar,
  Maximize2,
  XCircle,
} from "lucide-react";
import { useNotification } from "../../context/NotificationContext";

export default function MyRentalsDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { getRentalDetail, cancelRental, downloadRental } = useUserRentals();

  const [invoice, setInvoice] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);

  // Modal State
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchDetail = async () => {
    setIsLoading(true);
    const result = await getRentalDetail(id);
    if (result.success) {
      setInvoice(result.data);
    }
    setIsLoading(false);
  };

  const statusMap = {
    generated: { label: "Generated", color: "text-amber-600 bg-amber-50 border-amber-100" },
    approved: { label: "Approved", color: "text-blue-600 bg-blue-50 border-blue-100" },
    in_use: { label: "In Use", color: "text-indigo-600 bg-indigo-50 border-indigo-100" },
    completed: { label: "Completed", color: "text-green-600 bg-green-50 border-green-100" },
    cancelled: { label: "Cancelled", color: "text-red-600 bg-red-50 border-red-100" },
  };

  const handleDownload = async () => {
    if (!invoice?.id) return;
    setIsDownloading(true);

    try {
      const response = await downloadRental(invoice.id);
      const blob = response.data;

      if (!blob || blob.size === 0) {
        showNotification({
          type: "info",
          title: "File Unavailable",
          message: "The invoice PDF could not be found. It may still be generating."
        });
        return;
      }

      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `St-Stephens-Rentals-${invoice.invoice_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showNotification({
        type: "success",
        title: "Download Started",
        message: "Your invoice is being downloaded."
      });
    } catch (error) {
      console.error("Download error:", error);
      showNotification({
        type: "failed",
        title: "Download Failed",
        message: "Failed to download the invoice. Please try again."
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const confirmCancelRental = async () => {
    if (!invoice?.id) return;
    setIsCancelling(true);
    const result = await cancelRental(invoice.id);
    if (result.success) {
      setIsCancelModalOpen(false);
      showNotification({
        type: "success",
        title: "Invoice Cancelled",
        message: `Invoice ${invoice.invoice_number} has been successfully cancelled.`
      });
      fetchDetail(); // Refresh data to show cancelled status
    } else {
      showNotification({
        type: "failed",
        title: "Cancellation Failed",
        message: result.error || "Failed to cancel invoice. Please try again."
      });
    }
    setIsCancelling(false);
  };

  if (isLoading) {
    return (
      <UserLayout>
        <MyRentalsDetailSkeleton />
      </UserLayout>
    );
  }

  if (!invoice) {
    return (
      <UserLayout>
        <div className="p-8 text-center space-y-4 min-h-[60vh] flex flex-col items-center justify-center">
          <AlertCircle size={48} className="mx-auto text-red-500" />
          <h2 className="text-xl font-bold dark:text-white">Invoice Not Found</h2>
          <button
            onClick={() => navigate("/dashboard/rentals")}
            className="text-purple-600 font-semibold hover:underline"
          >
            Return to My Rentals
          </button>
        </div>
      </UserLayout>
    );
  }

  const badgedStatus = statusMap[invoice.status] || { label: invoice.status.replace('_', ' '), color: "text-gray-600 bg-gray-50 border-gray-100" };

  return (
    <UserLayout>
      <div className="p-4 md:p-8 lg:p-10 max-w-[1200px] mx-auto space-y-8 animate-fade-in pb-20">

        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 bg-white dark:bg-[#1A1A1A] border border-[#E5E7EB] dark:border-[#2A2A2A] shadow-sm hover:bg-[#F3F4F6] dark:hover:bg-[#262626] rounded-xl transition-colors"
            >
              <ArrowLeft size={20} className="text-[#6B7280] dark:text-[#A0A0A0]" />
            </button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-xl font-medium text-[#111111] dark:text-white font-serif uppercase">
                  {invoice.invoice_number}
                </h1>
                <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border shadow-sm ${badgedStatus.color}`}>
                  {badgedStatus.label}
                </span>
              </div>
              <p className="text-[#666666] dark:text-[#A0A0A0] text-sm mt-1">
                Created {new Date(invoice.created_at).toLocaleDateString()}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap md:flex-nowrap w-full md:w-auto">
            {invoice.status === 'generated' && (
              <button
                onClick={() => setIsCancelModalOpen(true)}
                className="flex-1 md:flex-none flex items-center justify-center gap-2 px-4 py-2.5 bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/20 text-red-600 dark:text-red-400 rounded-xl text-sm font-bold hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors shadow-sm"
              >
                <XCircle size={16} />
                <span>Cancel</span>
              </button>
            )}

            <button
              onClick={handleDownload}
              disabled={isDownloading}
              className="flex-1 md:flex-none flex items-center justify-center gap-2 px-5 py-2.5 bg-[#62397f] hover:bg-[#7C3AED] text-white rounded-xl text-sm font-bold shadow-md transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:scale-100"
            >
              {isDownloading ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Download size={16} />
              )}
              <span>Download PDF</span>
            </button>
          </div>
        </div>

        {/* Invoice PDF Viewer */}
        <div className="bg-white dark:bg-[#1A1A1A] rounded-2xl border border-[#E5E7EB] dark:border-[#2A2A2A] overflow-hidden shadow-sm h-[800px] relative">
          <iframe
            src={`${invoice.pdf_url}#toolbar=0`}
            className="w-full h-full border-none"
            title="Invoice PDF Viewer"
          />
          <div className="absolute bottom-6 right-6 flex gap-3">
            <a
              href={invoice.pdf_url}
              target="_blank"
              rel="noopener noreferrer"
              className="p-3 bg-white/90 dark:bg-[#1A1A1A]/90 backdrop-blur-sm rounded-full border border-[#E5E7EB] dark:border-[#2A2A2A] text-[#111111] dark:text-white hover:bg-[#62397f] hover:text-white transition-colors shadow-lg"
              title="Open Fullscreen"
            >
              <Maximize2 size={20} />
            </a>
          </div>
        </div>

        {/* Info Grid (Event Details) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white dark:bg-[#1A1A1A] p-6 rounded-2xl border border-[#E5E7EB] dark:border-[#2A2A2A] shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-blue-50 dark:bg-blue-900/20 text-blue-600 rounded-lg">
                <Calendar size={18} />
              </div>
              <h3 className="text-sm font-medium text-[#111111] dark:text-white font-serif">Event Dates</h3>
            </div>
            <p className="text-sm text-[#4B5563] dark:text-[#A0A0A0] font-medium leading-relaxed">
              {new Date(invoice.rental_start).toLocaleDateString()} — {new Date(invoice.rental_end).toLocaleDateString()}
            </p>
          </div>

          <div className="bg-white dark:bg-[#1A1A1A] p-6 rounded-2xl border border-[#E5E7EB] dark:border-[#2A2A2A] shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-purple-50 dark:bg-purple-900/20 text-purple-600 rounded-lg">
                <Map size={18} />
              </div>
              <h3 className="text-sm font-medium text-[#111111] dark:text-white font-serif">Event Type</h3>
            </div>
            <p className="text-sm text-[#4B5563] dark:text-[#A0A0A0] font-medium leading-relaxed capitalize">
              {invoice.event_type ? invoice.event_type.replace('_', ' ') : 'General Rental'}
            </p>
          </div>

          <div className="bg-white dark:bg-[#1A1A1A] p-6 rounded-2xl border border-[#E5E7EB] dark:border-[#2A2A2A] shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-amber-50 dark:bg-amber-900/20 text-amber-600 rounded-lg">
                <MapPin size={18} />
              </div>
              <h3 className="text-sm font-medium text-[#111111] dark:text-white font-serif">Venue Details</h3>
            </div>
            <p className="text-sm text-[#4B5563] dark:text-[#A0A0A0] font-medium leading-relaxed">
              {invoice.event_venue_address || "None specified"}
              {invoice.event_location_level ? ` (${invoice.event_location_level.replace('_', ' ')})` : ''}
            </p>
          </div>
        </div>

        {/* Confirm Cancel Modal */}
        <ConfirmCancelModal
          isOpen={isCancelModalOpen}
          onClose={() => setIsCancelModalOpen(false)}
          onConfirm={confirmCancelRental}
          invoiceNumber={invoice?.invoice_number}
          isLoading={isCancelling}
        />

      </div>
      <style jsx="true">{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.5s ease-out forwards;
        }
      `}</style>
    </UserLayout>
  );
}

// Reused Component from MyRentals for consistency
const ConfirmCancelModal = ({ isOpen, onClose, onConfirm, invoiceNumber, isLoading }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white dark:bg-[#1A1A1A] w-full max-w-md rounded-2xl border border-[#E5E7EB] dark:border-[#2A2A2A] shadow-2xl p-6 lg:p-8 transform transition-all animate-in zoom-in-95 duration-200">
        <div className="flex flex-col items-center text-center">
          <div className="w-16 h-16 bg-red-50 dark:bg-red-900/10 rounded-full flex items-center justify-center text-red-500 mb-6 border border-red-100 dark:border-red-900/20">
            <AlertCircle size={32} />
          </div>

          <h3 className="text-xl font-medium text-[#111111] dark:text-white mb-2 font-serif">
            Cancel Invoice?
          </h3>
          <p className="text-[#6B7280] dark:text-[#A0A0A0] text-sm leading-relaxed mb-8">
            Are you sure you want to cancel Invoice <strong>{invoiceNumber}</strong>? This action is permanent and cannot be undone.
          </p>

          <div className="grid grid-cols-2 gap-3 w-full">
            <button
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2.5 border border-[#E5E7EB] dark:border-[#2A2A2A] text-[#4B5563] dark:text-[#A0A0A0] rounded-xl text-sm font-semibold hover:bg-[#F9FAFB] dark:hover:bg-[#262626] transition-colors disabled:opacity-50"
            >
              No, Keep It
            </button>
            <button
              onClick={onConfirm}
              disabled={isLoading}
              className="px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-semibold shadow-lg shadow-red-200 dark:shadow-none transition-all active:scale-95 flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {isLoading ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                "Yes, Cancel"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
